import { pick } from 'ramda'
import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'

import { ArrayElement } from 'core/actions/Action'
import ListContainer from 'core/containers/ListContainer'
import DataKeys from 'k8s/DataKeys'

import { listTablePrefs, TablePrefsParams } from 'app/constants'
import InferActionParams from 'core/actions/InferActionParams'
import { DateAndTime } from 'core/components/listTable/cells/DateCell'
import { GridViewColumn } from 'core/elements/grid/Grid'
import { GridRowMenuItemSpec } from 'core/elements/grid/hooks/useGridRowMenu'
import { SortingState } from 'core/elements/grid/hooks/useGridSorting'
import useGlobalParams from 'core/hooks/useGlobalParams'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import { createResourceLabelsCell } from 'k8s/components/common/entity/labels-and-annotations/helpers'
import useScopedPreferences from 'core/session/useScopedPreferences'

import CreateButton from 'core/components/buttons/CreateButton'
import DocumentMeta from 'core/components/DocumentMeta'
import Progress from 'core/components/progress/Progress'
import { createGridLinkCell } from 'core/elements/grid/cells/GridLinkCell'
import getGridDialogButton from 'core/elements/grid/helpers/getGridDialogButton'
import { GridDropdownBatchActionSpec } from 'core/elements/grid/hooks/useGridSelectableRows'
import { HeaderPrimaryActionPortal } from 'core/elements/header/portals'
import useSelectorWithParams from 'core/hooks/useSelectorWithParams'
import { routeManager } from 'core/utils/routes'
import { listCustomResourceDefinitions, listCustomResources } from '../../actions'
import {
  ICustomResourceDefinitionSelector,
  ICustomResourceDetailsPageTabs,
  ICustomResourcesSelector,
} from '../../model'
import { customResourceDefinitionsSelector, customResourcesSelector } from '../../selectors'
import AddCustomResourceModal from '../AddCustomResourceModal'
import DeleteCustomResourceDialog from './DeleteCustomResourceDialog'
import { K8sPluginGlobalPrefs } from 'k8s/model'
import useNamespaceFilterDropdown from 'k8s/components/useNamespaceFilterDropdown'

const searchTargets = ['name', 'clusterName']
type ModelDataKey = DataKeys.CustomResources
type SelectorModel = ArrayElement<ReturnType<typeof customResourcesSelector>>
type ActionParams = InferActionParams<typeof listCustomResources>

const usePrefParams = createUsePrefParamsHook<ActionParams & TablePrefsParams>(
  'CustomResources',
  listTablePrefs,
)

const defaultParams: ActionParams & SortingState = {
  clusterId: '',
  group: '',
  version: '',
  pluralName: '',
  orderBy: 'creationTimestamp',
  orderDirection: 'desc',
}

export const getCustomResourceDefinitionsColumns = (id): GridViewColumn<SelectorModel>[] => {
  return [
    {
      key: 'name',
      label: 'Name',
      CellComponent: createGridLinkCell({
        routeToFn: ({ clusterName, name }) =>
          routeManager.routes.kubernetes.resources.customResourceDefinitions.customResourceDetails.path(
            {
              cluster: clusterName,
              id,
              name,
              tab: ICustomResourceDetailsPageTabs.Overview,
            },
          ),
      }),
    },
    {
      key: 'namespace',
      label: 'Namespace',
      // Return default as namespace if namespace is missing
      formatFn: (_v, item) => {
        return item.namespace
      },
    },
    {
      key: 'kind',
      label: 'Kind',
    },
    {
      key: 'phase',
      label: 'Phase',
    },
    {
      key: 'creationTimestamp',
      label: 'Created',
      CellComponent: DateAndTime,
    },
    {
      key: 'labels',
      label: 'Labels',
      disableSorting: true,
      CellComponent: createResourceLabelsCell({ type: 'table', separator: '=' }),
    },
  ]
}

function CustomResourcesList() {
  const { allParams: params, getParamsUpdater, updateGlobalParams } = useGlobalParams(
    usePrefParams,
    defaultParams,
  )
  const { match } = useReactRouter()
  const { id } = match.params
  const { prefs: k8sPluginGlobalPerfs } = useScopedPreferences<K8sPluginGlobalPrefs>(
    'k8sPluginGlobalParams',
  )
  const clusterId = k8sPluginGlobalPerfs?.clusterId || ''
  const clusterName = k8sPluginGlobalPerfs?.cluster || ''

  const [selectedCustomResource, setSelectedCustomResource] = useState<SelectorModel>(null)
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false)

  const {
    loading: loadingCustomResourceDefinitions,
    reload: reloadCustomResourceDefinitions,
  } = useListAction(listCustomResourceDefinitions, {
    params: { clusterId },
  })
  const customResourceDefinitions: ICustomResourceDefinitionSelector[] = useSelector(
    customResourceDefinitionsSelector,
  )
  const customResourceDefinition = useMemo(
    () =>
      customResourceDefinitions.find(
        (customResourceDefinition) => customResourceDefinition?.id === id,
      ),
    [id, customResourceDefinitions],
  )

  const {
    group = '',
    latestVersion: version = '',
    spec: { names: { plural: pluralName } } = { names: { plural: '' } },
  } = customResourceDefinition || {}

  const { loading, reload } = useListAction(listCustomResources, {
    params: { clusterId, group, version, pluralName },
  })
  const customResources: ICustomResourcesSelector[] = useSelectorWithParams(
    customResourcesSelector,
    {
      clusterId,
      group,
      version,
      pluralName,
    },
  )
  const columns = getCustomResourceDefinitionsColumns(id)
  const rowMenuItems: Array<GridRowMenuItemSpec<SelectorModel>> = [
    {
      label: 'Delete',
      icon: 'trash-alt',
      handleClick: (customResource) => {
        setSelectedCustomResource(customResource)
        setShowDeleteDialog(true)
      },
      refreshAfterSuccess: true,
    },
  ]

  const batchActions: GridDropdownBatchActionSpec<SelectorModel>[] = useMemo(
    () => [
      {
        label: 'Delete',
        BatchActionButton: getGridDialogButton(DeleteCustomResourceDialog, {
          action: 'delete',
        }),
      },
    ],
    [],
  )

  const { Component, selectedNs } = useNamespaceFilterDropdown()
  const filteredCustomResources = customResources?.filter((item) =>
    selectedNs?.includes(item?.namespace),
  )
  useEffect(() => {
    if (clusterId) {
      reloadCustomResourceDefinitions(true)
      reload(true)
    }
  }, [clusterId])
  return (
    <Progress loading={loading || loadingCustomResourceDefinitions}>
      <DocumentMeta title="Custom Resource" />
      <HeaderPrimaryActionPortal>
        {showDeleteDialog && (
          <DeleteCustomResourceDialog
            onClose={() => setShowDeleteDialog(false)}
            rows={[selectedCustomResource]}
            data-testid="delete-custom-resource-dialog"
          />
        )}
      </HeaderPrimaryActionPortal>
      <AddCustomResourceModal data-testid="add-custom-resource-modal" />

      <ListContainer<ModelDataKey, SelectorModel>
        dataKey={DataKeys.CustomResources}
        searchTargets={searchTargets}
        uniqueIdentifier="id"
        loading={loading || loadingCustomResourceDefinitions}
        loadingMessage={'Loading Custom Resources'}
        onRefresh={reload}
        data={filteredCustomResources}
        columns={columns}
        addUrl={routeManager.routes.kubernetes.resources.customResourceDefinitions.customResourceAdd.path(
          {
            cluster: clusterName,
            id,
          },
        )}
        addText={'Add Custom Resource'}
        rowMenuItems={rowMenuItems}
        getParamsUpdater={getParamsUpdater}
        showItemsCountInLabel
        showRowMenuForSingleRowActions
        disableColumnOrdering
        showBreadcrumbs={false}
        multiSelection
        batchActions={batchActions}
        extraToolbarContent={Component}
        {...pick(listTablePrefs, params)}
        data-testid="custom-resources-list-container"
      />
    </Progress>
  )
}

export default CustomResourcesList
